import React, { FC } from 'react';
import moment from 'moment';

import { Routes } from '@common/constants';
import SectionContainer from '@common/components/SectionContainer';
import { useRouter } from 'shared-routing';
import { useTranslations } from '@lang';
import { useCheckForArrivalDate } from '@common/hooks';
import { DateCategoryPicker } from '@features/JourneyPicker';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { SpaEntityError } from '@common/components/SpaEntity/SpaEntityError';

import { SpaListTemplate } from 'voyager';

import { FacilitiesV2Props } from './FacilitiesV2.types';
import { useSpaEntityFilter } from '../../../../spa/pages/SpaTreatments/v2/hooks/useSpaEntityFilter';
import { useFacilitiesList } from './hooks/useFacilitiesList';

export const FacilitiesV2: FC<FacilitiesV2Props> = ({
  property,
  journeyParams,
  changeJourneyParam,
  facilitiesCategories = [],
  categoryId = null,
  userHasAccessToFacilities = false,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.FACILITY_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
    changeJourneyParam,
    listingRoute: Routes.FACILITY_LIST,
  });

  const { listItems, isFetching } = useFacilitiesList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    date: filter.appliedDateString,
  });

  const facilitiesVisibleToMembers =
    property?.modulesExcludingDisabled?.spa?.features?.facilities?.features?.visibleToMembers
      ?.enabled;

  if (!userHasAccessToFacilities) {
    return (
      <SpaEntityError
        errorMessage={facilitiesVisibleToMembers ? 'app.mustBeMember' : 'app.mustBeLoggedIn'}
        values={{ spaEntity: 'facilities' }}
      />
    );
  }

  const allCategoriesOption = {
    label: t('common.allCategories'),
    value: '',
  };

  const categoryOptions = [
    allCategoriesOption,
    ...facilitiesCategories.map((category) => ({
      label: category.name,
      value: String(category.id),
    })),
  ];

  const currentSelectedCategory = categoryOptions.find(
    ({ value }) => String(value) === String(filter.selectedCategory)
  );

  const handleSelectFacility = (id: string) => {
    router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } });
  };

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <DateCategoryPicker
            fromDate={moment(filter.pickerDate)}
            onDateChange={(d) => filter.setPickerDate(d.format())}
            categories={categoryOptions}
            onCategoryChange={filter.handleCategoryChange}
            selectedCategory={currentSelectedCategory}
            handleUpdate={filter.handleUpdate}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <SpaListTemplate
          items={isFetching ? [] : listItems}
          onSelectItem={handleSelectFacility}
          isLoading={isFetching}
          categoryOptions={categoryOptions}
          selectedCategory={filter.selectedCategory || ''}
          onCategoryChange={filter.handleDirectCategoryChange}
          titleLabel={t('app.facilities')}
          categoryLabel={t('common.category')}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('spa.soldOut')}
          noResultsLabel={t('spa.noResults')}
          noImageLabel={t('spa.noImage')}
          formatGuests={(n) => t('common.numberOfGuests', { guestsAmount: n })}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
